import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { AlertTriangle, Building2, Mail, MonitorSmartphone, Store, Users } from 'lucide-react';
import {
  Alert,
  Badge,
  Card,
  CardHeader,
  EmptyState,
  LicenseBadge,
  PageHeader,
  PageLoader,
  StatCard,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  formatDate,
} from '@shripos/ui';
import { api } from '../../lib/api.js';

const inr = (paise) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(
    (paise ?? 0) / 100,
  );

/** Today vs yesterday and a 7-day bar strip, from the per-outlet daily summaries. */
function SalesCard({ sales }) {
  if (!sales) return <div className="px-5 py-4 text-sm text-slate-500">No sales data yet.</div>;
  const max = Math.max(1, ...sales.last7.map((d) => d.totalPaise));
  const change = sales.yesterdayPaise
    ? Math.round(((sales.todayPaise - sales.yesterdayPaise) * 100) / sales.yesterdayPaise)
    : null;
  return (
    <div className="space-y-3 px-5 py-4">
      <div>
        <p className="text-2xl font-semibold tabular-nums">{inr(sales.todayPaise)}</p>
        <p className="text-xs text-slate-500">
          today · {sales.todayBills} bills · {sales.activeClientsToday} active clients
          {change != null && (
            <span className={change >= 0 ? ' text-emerald-700' : ' text-red-700'}>
              {' '}
              · {change >= 0 ? '+' : ''}
              {change}% vs yesterday
            </span>
          )}
        </p>
      </div>
      <div className="flex h-16 items-end gap-1" aria-label="Sales, last 7 days">
        {sales.last7.map((d) => (
          <div
            key={d.date}
            className="flex flex-1 flex-col items-center gap-1"
            title={`${d.date}: ${inr(d.totalPaise)}`}
          >
            <div
              className="w-full rounded-t bg-brand-500"
              style={{ height: `${Math.max(2, (d.totalPaise / max) * 56)}px` }}
            />
            <span className="text-[10px] text-slate-400">{d.date.slice(8)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get('/dashboard'),
    refetchInterval: 60_000,
  });
  if (isLoading) return <PageLoader />;
  if (error) return <Alert tone="danger">{error.message}</Alert>;
  const s = data.clients.byStatus;
  const go = (q) => navigate(`/clients${q}`);

  return (
    <>
      <PageHeader title="Dashboard" description="All clients at a glance. Refreshes every minute." />

      {!data.smtp?.ok && (
        <Alert tone="danger" title="Email is not working" className="mb-5">
          {data.smtp?.error ?? 'SMTP has not been verified.'} Welcome, reset and reminder emails are queued until it
          works.
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <StatCard label="Total clients" value={data.clients.total} icon={Building2} onClick={() => go('')} />
        <StatCard label="Active" value={s.active} tone="success" onClick={() => go('?status=active')} />
        <StatCard label="Expiring soon" value={s.expiring} tone="warning" onClick={() => go('?status=expiring')} />
        <StatCard label="In grace" value={s.grace} tone="danger" onClick={() => go('?status=grace')} />
        <StatCard label="Expired" value={s.expired} tone="danger" onClick={() => go('?status=expired')} />
        <StatCard label="Suspended" value={s.suspended} onClick={() => go('?status=suspended')} />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <StatCard
          label="Expiring in 7 days"
          value={data.clients.expiringIn[7]}
          tone="warning"
          onClick={() => go('?expiringWithin=7')}
        />
        <StatCard
          label="Expiring in 15 days"
          value={data.clients.expiringIn[15]}
          onClick={() => go('?expiringWithin=15')}
        />
        <StatCard
          label="Expiring in 30 days"
          value={data.clients.expiringIn[30]}
          onClick={() => go('?expiringWithin=30')}
        />
        <StatCard
          label="Outlets online"
          value={`${data.outletsOnline} / ${data.outlets}`}
          icon={Store}
          hint="a device sent a heartbeat in the last 2 min"
        />
        <StatCard label="Devices online" value={`${data.devicesOnline} / ${data.devices}`} icon={MonitorSmartphone} />
        <StatCard label="Staff users" value={data.users} icon={Users} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Recent sign-ups" />
          {data.recentSignups.length === 0 ? (
            <EmptyState
              icon={Building2}
              title="No clients yet"
              description="Create your first client from the Clients page."
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Client</TH>
                  <TH>Plan</TH>
                  <TH>Status</TH>
                  <TH>Created</TH>
                </TR>
              </THead>
              <TBody>
                {data.recentSignups.map((c) => (
                  <TR key={c.id} onClick={() => navigate(`/clients/${c.id}`)}>
                    <TD>
                      <p className="font-medium text-slate-900">{c.businessName}</p>
                      <p className="text-xs text-slate-500">{c.ownerName}</p>
                    </TD>
                    <TD className="capitalize">{c.planCode}</TD>
                    <TD>
                      <LicenseBadge status={c.status} />
                    </TD>
                    <TD>{formatDate(c.createdAt)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Sales across clients" />
            <SalesCard sales={data.sales} />
          </Card>
          <Card>
            <CardHeader
              title="Email"
              actions={
                <Badge tone={data.smtp?.ok ? 'success' : 'danger'}>{data.smtp?.ok ? 'Healthy' : 'Failing'}</Badge>
              }
            />
            <div className="space-y-2 px-5 py-4 text-sm">
              <button
                onClick={() => navigate('/emails?status=pending')}
                className="flex w-full items-center justify-between hover:text-brand-700"
              >
                <span className="flex items-center gap-2">
                  <Mail className="size-4 text-slate-400" /> Waiting to send
                </span>
                <span className="font-semibold tabular-nums">{data.emailsPending}</span>
              </button>
              <button
                onClick={() => navigate('/emails?status=failed')}
                className="flex w-full items-center justify-between hover:text-brand-700"
              >
                <span className="flex items-center gap-2">
                  <AlertTriangle className="size-4 text-slate-400" /> Failed (24 h)
                </span>
                <span className={`font-semibold tabular-nums ${data.emailsFailed24h ? 'text-red-600' : ''}`}>
                  {data.emailsFailed24h}
                </span>
              </button>
              <p className="pt-1 text-xs text-slate-500">Mode: {data.smtp?.mode}</p>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
